import { useCallback, useEffect, useState } from 'react';
import { masterDataAPI } from '../services/api';
import { EntityOption } from '../components/common/SearchableEntitySelect';

export interface MasterDataRow {
  _id: string;
  name: string;
  code?: string;
  parentId?: string;
  vehicleTypeId?: string;
}

function asId(value: unknown): string {
  if (!value) return '';
  if (typeof value === 'object' && value !== null && '_id' in (value as object)) {
    return String((value as { _id: unknown })._id);
  }
  return String(value);
}

export function useVehicleMasterData(enabled: boolean) {
  const [types, setTypes] = useState<MasterDataRow[]>([]);
  const [makes, setMakes] = useState<MasterDataRow[]>([]);
  const [models, setModels] = useState<MasterDataRow[]>([]);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    if (!enabled) return;
    setLoading(true);
    try {
      const res = await masterDataAPI.getTree();
      setTypes(res.data?.types || []);
      setMakes(res.data?.makes || []);
      setModels(res.data?.models || []);
    } catch {
      setTypes([]);
      setMakes([]);
      setModels([]);
    } finally {
      setLoading(false);
    }
  }, [enabled]);

  useEffect(() => { load(); }, [load]);

  const toOptions = (rows: MasterDataRow[]): EntityOption[] =>
    rows.map((r) => ({ _id: String(r._id), label: r.name, sublabel: r.code }));

  const typeOptions = toOptions(types);

  const makesForType = useCallback((typeId: string): EntityOption[] => {
    // Makes are not type-specific; always offer the full Super Admin make list.
    void typeId;
    return toOptions(makes);
  }, [makes]);

  const modelsForMake = useCallback((makeId: string, typeId?: string): EntityOption[] => {
    if (!makeId) return [];
    const make = asId(makeId);
    const type = asId(typeId);
    const forMake = models.filter((m) => {
      const parent = asId(m.parentId);
      return !parent || parent === make;
    });
    const typed = type
      ? forMake.filter((m) => !m.vehicleTypeId || asId(m.vehicleTypeId) === type)
      : forMake;
    if (typed.length) return toOptions(typed);
    if (forMake.length) return toOptions(forMake);
    if (type) {
      const forType = models.filter((m) => !m.vehicleTypeId || asId(m.vehicleTypeId) === type);
      if (forType.length) return toOptions(forType);
    }
    return toOptions(models);
  }, [models]);

  const resolveIdsFromNames = useCallback((vehicleType?: string, brand?: string, vehicleModel?: string) => {
    const norm = (s?: string) => String(s || '').trim().toLowerCase();
    const typeHit = types.find((t) => norm(t.name) === norm(vehicleType));
    const makeHit = makes.find((m) => norm(m.name) === norm(brand));
    const modelHit = models.find((m) => {
      const name = norm(m.name);
      const wanted = norm(vehicleModel);
      return name === wanted || (wanted && name.includes(wanted)) || (wanted && wanted.includes(name));
    });
    return {
      typeId: typeHit ? String(typeHit._id) : '',
      makeId: makeHit ? String(makeHit._id) : '',
      modelId: modelHit ? String(modelHit._id) : '',
    };
  }, [types, makes, models]);

  return {
    loading,
    typeOptions,
    makesForType,
    modelsForMake,
    resolveIdsFromNames,
    reload: load,
    modelCount: models.length,
  };
}
